import { useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  NoticeHost,
  useNotice,
} from '@backrs_ui/grow-ui/src/components/InstitutionWrite/NoticeHost';
import { refusalProse } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/refusalProse';
import type { WriteOutcome } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/writeOutcome';
import { useOperatorInstitutionSwitch } from '@backrs_ui/grow-ui/src/hooks/useOperatorInstitutionSwitch';
import { useSandboxGate } from '@backrs_ui/grow-ui/src/hooks/useSandboxGate';
import {
  institutionLabel,
  offeringLabel,
} from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import { OperatorRootPicker } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/OperatorRootPicker';
import { CreateCodeDialog } from '@backrs_ui/grow-ui/src/pages/RostersAdmin/CreateCodeDialog';
import {
  codeStrings,
  revokeStrings,
} from '@backrs_ui/grow-ui/src/pages/RostersAdmin/institutionAdminWriteMessages';
import { RevokeCodeDialog } from '@backrs_ui/grow-ui/src/pages/RostersAdmin/RevokeCodeDialog';
import { rosterLineKey } from '@backrs_ui/grow-ui/src/pages/RostersAdmin/rosterCodes';
import { RostersSection } from '@backrs_ui/grow-ui/src/pages/RostersAdmin/RostersSection';
import { SeatSummary } from '@backrs_ui/grow-ui/src/pages/RostersAdmin/SeatSummary';
import { useCodeDoors } from '@backrs_ui/grow-ui/src/pages/RostersAdmin/useCodeDoors';
import {
  firstOperatorContextRefusal,
  useGetAdminRostersQuery,
  type AdminCode,
  type AdminRoster,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { Box, type SxProps, type Theme } from '@mui/material';

import { useAgentContext } from './SeatsTab.agentContext';

const strings = appStrings.modules.rostersAdmin;

/** The line a code door was opened from, and for a revoke the code that line shows. */
type CodeTarget = Readonly<{ roster: AdminRoster; lineKey: string }>;
type RevokeTarget = CodeTarget & Readonly<{ code: AdminCode }>;

const rosterContextLabel = (roster: AdminRoster): string =>
  strings.rosterOptionLabel(
    offeringLabel(roster.offering),
    institutionLabel(roster.managingInstitution),
  );

const SeatsTabContent = () => {
  const rosters = useGetAdminRostersQuery({});
  const onOperatorInstitutionSelect = useOperatorInstitutionSwitch();
  const codeDoors = useCodeDoors();
  const { announce } = useNotice();
  const gateInSandbox = useSandboxGate();

  const [createCodeTarget, setCreateCodeTarget] = useState<CodeTarget | null>(null);
  const [codeDraft, setCodeDraft] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);

  const heldRoots = firstOperatorContextRefusal([rosters.error, ...codeDoors.errors]);

  useAgentContext(
    rosters.data?.data ?? [],
    revokeTarget !== null ? 'revoke' : createCodeTarget !== null ? 'mint' : null,
    revokeTarget?.roster.id ?? createCodeTarget?.roster.id ?? null,
  );

  const say = (outcome: WriteOutcome, fallback: string) =>
    announce(outcome.message ?? fallback, outcome.ok ? 'success' : 'error');

  /**
   * Every door onto a write goes through here. In the sandbox it opens the contact prompt and
   * the door stays shut, so a prospect sees what the action is without the page changing under
   * them; everywhere else it opens as normal.
   */
  const door =
    <Args extends unknown[]>(open: (...args: Args) => void) =>
    (...args: Args) => {
      if (gateInSandbox()) return;

      open(...args);
    };

  const createCode = async () => {
    if (createCodeTarget === null) return;

    const outcome = await codeDoors.codeCreate.onCreate(
      createCodeTarget.roster.id,
      null,
      createCodeTarget.lineKey,
      codeDraft,
    );

    if (outcome.ok) setCreateCodeTarget(null);

    say(outcome, codeStrings.mint.error);
  };

  const confirmRevoke = async () => {
    if (revokeTarget === null) return;

    const outcome = await codeDoors.codeRevoke.onRevoke(
      revokeTarget.roster.id,
      revokeTarget.code.id,
      revokeTarget.lineKey,
    );

    if (outcome.ok) setRevokeTarget(null);

    say(outcome, revokeStrings.failed);
  };

  return (
    <Box sx={containerSx}>
      {heldRoots !== null ? (
        <OperatorRootPicker heldRoots={heldRoots} onSelect={onOperatorInstitutionSelect} />
      ) : (
        <>
          <SeatSummary rosters={rosters.data?.data ?? []} />
          <RostersSection
            rows={rosters.data?.data ?? []}
            isLoading={rosters.isFetching}
            isError={rosters.isError}
            errorMessage={refusalProse(rosters.error)}
            dropped={rosters.data?.dropped ?? 0}
            doors={{
              isCreatingFor: codeDoors.codeCreate.isPendingFor,
              onCreateCode: door((roster: AdminRoster) => {
                codeDoors.codeCreate.reset();
                setCodeDraft('');
                setCreateCodeTarget({ roster, lineKey: rosterLineKey(roster.id) });
              }),
              onRevoke: door((roster: AdminRoster, code: AdminCode) => {
                codeDoors.codeRevoke.reset();
                setRevokeTarget({ roster, code, lineKey: rosterLineKey(roster.id) });
              }),
            }}
          />
        </>
      )}
      {createCodeTarget !== null ? (
        <CreateCodeDialog
          open
          onClose={() => setCreateCodeTarget(null)}
          subjectName={offeringLabel(createCodeTarget.roster.offering)}
          rosterName={rosterContextLabel(createCodeTarget.roster)}
          code={codeDraft}
          onCodeChange={setCodeDraft}
          isSubmitting={codeDoors.codeCreate.isPendingFor(createCodeTarget.lineKey)}
          isWriteInFlight={codeDoors.isWriteInFlight}
          isError={codeDoors.codeCreate.isErrorFor(createCodeTarget.lineKey)}
          errorMessage={codeDoors.codeCreate.errorMessage}
          onSubmit={() => void createCode()}
        />
      ) : null}
      {revokeTarget !== null ? (
        <RevokeCodeDialog
          open
          onClose={() => setRevokeTarget(null)}
          subjectName={offeringLabel(revokeTarget.roster.offering)}
          code={revokeTarget.code}
          isSubmitting={codeDoors.codeRevoke.isPendingFor(revokeTarget.lineKey)}
          isWriteInFlight={codeDoors.isWriteInFlight}
          isError={codeDoors.codeRevoke.isErrorFor(revokeTarget.lineKey)}
          errorMessage={codeDoors.codeRevoke.errorMessage}
          onConfirm={() => void confirmRevoke()}
        />
      ) : null}
    </Box>
  );
};

export const SeatsTab = () => (
  <NoticeHost id="seats-tab-notice">
    <SeatsTabContent />
  </NoticeHost>
);

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  marginTop: backrs.spacing['spacing-xl'],
});
